import { queryOptions } from '@tanstack/react-query'
import type { CatalogResponse, Definition, RunResult, ValueItem } from '../reports/types'
import { apiExchange, apiGet, apiPostJson } from './client'

/**
 * Zona de rapoarte dinamice (/app/reports). Capetele, din `ReportsController`:
 *
 *   GET  /reports/catalog?company_id=          seturile vazute pe compania aleasa
 *   GET  /reports/values?company_id=&dataset=&field=   valorile reale ale unui camp
 *   POST /reports/run      {company_id, definition}
 *   POST /reports/export   {company_id, definition, format, locale}
 *
 * Definitia e numai chei. Perimetrul (compania, sediile omului, dreptul pe set)
 * il pune serverul; ce trimite browserul doar ingusteaza.
 */

export function reportsCatalogQuery(companyId: number) {
  return queryOptions({
    queryKey: ['reports', 'catalog', companyId],
    queryFn: () => apiGet<CatalogResponse>(`/reports/catalog${companyId > 0 ? `?company_id=${companyId}` : ''}`),
    staleTime: 5 * 60 * 1000,
  })
}

export function reportsValuesQuery(companyId: number, dataset: string, field: string) {
  return queryOptions({
    queryKey: ['reports', 'values', companyId, dataset, field],
    queryFn: () =>
      apiGet<{ values: ValueItem[] }>(
        `/reports/values?company_id=${companyId}&dataset=${encodeURIComponent(dataset)}&field=${encodeURIComponent(field)}`
      ).then((r) => r.values),
    /* Vocabularul barei: cache scurt, cum cere planul. */
    staleTime: 2 * 60 * 1000,
  })
}

export function runReport(companyId: number, definition: Definition): Promise<RunResult> {
  return apiPostJson<RunResult>('/reports/run', { company_id: companyId, definition })
}

export type ExportFormat = 'csv' | 'xlsx' | 'pdf'

/** Descarcarea: fisierul vine din raspuns si se salveaza din browser. */
export async function exportReport(companyId: number, definition: Definition, format: ExportFormat, locale: string): Promise<void> {
  const out = await apiExchange('/reports/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company_id: companyId, definition, format, locale }),
  })
  const url = URL.createObjectURL(new Blob([out.bytes], { type: out.type }))
  const a = document.createElement('a')
  a.href = url
  a.download = out.filename || `report.${format}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
